import React, { forwardRef, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import Popup, {
	type PopupComponentProps,
	type PopupProps,
	type TriggerProps,
} from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { MenuSection } from './chat-header-menu-list';

const styles = cssMap({
	menuPopupContainer: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		boxShadow: token('elevation.shadow.overlay'),
		overflow: 'hidden',
	},
	contentBoxTransition: {
		transition: 'width 0.2s ease, max-width 0.2s ease',
	},
});

export interface ChatHeaderMenuProps {
	testId?: string;
	sections: MenuSection[];
	isOpen: boolean;
	placement: PopupProps['placement'];
	width: string;
	maxWidth: string;
	onClose: () => void;
	trigger: (triggerProps: TriggerProps) => ReactNode;
	zIndex?: number;
	content: ReactNode;
	popoverAutoFocus?: boolean;
	/**
	 * Renders the popup inside the trigger's DOM tree instead of a portal.
	 *
	 * Needed by consumers that live inside a dismiss-on-outside-interaction
	 * surface (e.g. the editor's inline AI prompt), where a portalled menu reads
	 * as an outside click and tears the surface down.
	 */
	shouldRenderToParent?: boolean;
}

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<Box
		{...props}
		ref={ref}
		xcss={styles.menuPopupContainer}
		// PopupComponent's data-testid is of type string, while Box only accepts undefined
		data-testid={undefined}
	/>
));

export const ChatHeaderMenu = ({
	testId,
	sections,
	isOpen,
	onClose,
	trigger,
	placement,
	width,
	maxWidth,
	zIndex = 600,
	content,
	popoverAutoFocus,
	shouldRenderToParent,
}: ChatHeaderMenuProps): React.JSX.Element | null => {
	if (!sections) {
		return null;
	}

	const boxStyle = { width, maxWidth };

	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			placement={placement}
			trigger={trigger}
			popupComponent={PopupComponent}
			zIndex={zIndex}
			autoFocus={popoverAutoFocus}
			shouldRenderToParent={shouldRenderToParent}
			testId={testId}
			content={() => (
				<Box xcss={styles.contentBoxTransition} style={boxStyle}>
					{content}
				</Box>
			)}
		/>
	);
};
